<template>
  <v-container>
    <v-data-table
      :headers="headers"
      :items="solicitudesGasto"
      item-key="id"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Solicitudes de Gasto</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="flat" @click="openDialogForNew">
            Nueva Solicitud
          </v-btn>
        </v-toolbar>
        <v-row class="px-4 pb-2" dense>
          <v-col cols="12" md="3">
            <v-select
              v-model="filtros.estado"
              :items="estados"
              item-title="title"
              item-value="value"
              label="Estado"
              clearable
              density="compact"
              @update:model-value="recargar"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filtros.categoria_gasto_id"
              :items="categoriasGasto"
              item-title="descripcion"
              item-value="id"
              label="Categoría"
              clearable
              density="compact"
              @update:model-value="recargar"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filtros.fecha_inicio"
              label="Desde"
              type="date"
              density="compact"
              @update:model-value="recargar"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filtros.fecha_fin"
              label="Hasta"
              type="date"
              density="compact"
              @update:model-value="recargar"
            />
          </v-col>
        </v-row>
      </template>

      <template v-slot:[`item.fecha`]="{ item }">
        {{ moment(item.fecha).format('DD/MM/YYYY') }}
      </template>

      <template v-slot:[`item.categoria`]="{ item }">
        {{ item.categoria_gasto?.descripcion }}
      </template>

      <template v-slot:[`item.auto`]="{ item }">
        <span v-if="item.auto">{{ item.auto.placa }}</span>
        <span v-else class="text-grey">—</span>
      </template>

      <template v-slot:[`item.solicitante`]="{ item }">
        {{ item.solicitante?.name }}
      </template>

      <template v-slot:[`item.monto`]="{ item }">
        <v-chip color="primary" size="small">S/ {{ Number(item.monto).toFixed(2) }}</v-chip>
      </template>

      <template v-slot:[`item.estado`]="{ item }">
        <v-tooltip :disabled="!item.revisado_por" location="top">
          <template v-slot:activator="{ props }">
            <v-chip v-bind="props" :color="colorEstado(item.estado)" size="small" variant="flat">
              {{ textoEstado(item.estado) }}
            </v-chip>
          </template>
          <div>
            {{ item.revisado_por?.name }} —
            {{ item.fecha_revision ? moment(item.fecha_revision).format('DD/MM/YYYY HH:mm') : '' }}
          </div>
          <div v-if="item.observacion">{{ item.observacion }}</div>
        </v-tooltip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <template v-if="item.estado === 'pendiente'">
          <template v-if="puedeAprobar">
            <v-icon class="me-2" size="small" color="green" title="Aprobar" @click="abrirRevision(item, 'aprobar')">mdi-check-circle</v-icon>
            <v-icon class="me-2" size="small" color="orange" title="Rechazar" @click="abrirRevision(item, 'rechazar')">mdi-close-circle</v-icon>
          </template>
          <template v-if="puedeAprobar || item.solicitante_id === $store.state.user?.id">
            <v-icon class="me-2" size="small" @click="editItem(item)">mdi-pencil</v-icon>
            <v-icon size="small" color="red" @click="confirmDelete(item)">mdi-delete</v-icon>
          </template>
        </template>
      </template>
    </v-data-table>

    <GastoDialog
      :dialog="dialog"
      :editedItem="editedItem"
      :formTitle="formTitle"
      :categoriasGasto="categoriasGasto"
      :autos="autos"
      @update:dialog="dialog = $event"
      @save="saveDialog"
    />

    <v-dialog v-model="dialogRevision" max-width="500px" persistent>
      <v-card>
        <v-card-title>{{ revision.accion === 'aprobar' ? 'Aprobar Solicitud' : 'Rechazar Solicitud' }}</v-card-title>
        <v-card-text>
          <p class="mb-4">
            {{ revision.item?.descripcion }} — <strong>S/ {{ Number(revision.item?.monto || 0).toFixed(2) }}</strong>
          </p>
          <v-alert v-if="revision.accion === 'aprobar'" type="info" variant="tonal" density="compact" class="mb-4">
            Al aprobar se registrará como gasto y contará en el dashboard y el flujo.
          </v-alert>
          <v-textarea
            v-model="revision.observacion"
            :label="revision.accion === 'aprobar' ? 'Observación (opcional)' : 'Motivo del rechazo'"
            rows="2"
            auto-grow
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogRevision = false">Cancelar</v-btn>
          <v-btn
            :color="revision.accion === 'aprobar' ? 'green' : 'orange'"
            variant="flat"
            :loading="guardandoRevision"
            :disabled="revision.accion === 'rechazar' && !revision.observacion"
            @click="confirmarRevision"
          >
            {{ revision.accion === 'aprobar' ? 'Aprobar' : 'Rechazar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogDelete" width="auto" persistent>
      <v-card max-width="400" prepend-icon="mdi-delete" title="Eliminar Solicitud"
        text="¿Está seguro de eliminar esta solicitud?">
        <template v-slot:actions>
          <v-spacer />
          <v-btn color="blue-darken-1" text @click="dialogDelete = false">Cancelar</v-btn>
          <v-btn color="red-darken-1" text @click="deleteItemConfirm">Eliminar</v-btn>
        </template>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import GastoDialog from '@/components/gasto/GastoDialog.vue';

const ESTADOS = {
  pendiente: { title: 'Pendiente', color: 'orange' },
  aprobado: { title: 'Aprobado', color: 'green' },
  rechazado: { title: 'Rechazado', color: 'red' },
};

export default {
  components: { GastoDialog },
  data() {
    return {
      moment,
      dialog: false,
      dialogDelete: false,
      dialogRevision: false,
      guardandoRevision: false,
      headers: [
        { title: 'Fecha', value: 'fecha' },
        { title: 'Categoría', value: 'categoria' },
        { title: 'Descripción', value: 'descripcion' },
        { title: 'Auto', value: 'auto' },
        { title: 'Solicitante', value: 'solicitante' },
        { title: 'Monto', value: 'monto' },
        { title: 'Estado', value: 'estado' },
        { title: 'Acciones', value: 'actions', sortable: false },
      ],
      estados: Object.entries(ESTADOS).map(([value, e]) => ({ value, title: e.title })),
      editedIndex: -1,
      editedItem: {},
      revision: { item: null, accion: null, observacion: '' },
      filtros: {
        estado: 'pendiente',
        categoria_gasto_id: null,
        fecha_inicio: '',
        fecha_fin: '',
      },
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  computed: {
    ...mapGetters('gasto', ['solicitudesGasto', 'categoriasGasto', 'autos']),
    formTitle() {
      return this.editedIndex === -1 ? 'Nueva Solicitud de Gasto' : 'Editar Solicitud de Gasto';
    },
    puedeAprobar() {
      return this.$store.getters.can('solicitudes-gasto.aprobar');
    },
  },
  created() {
    this.recargar();
    this.fetchCategoriasGasto();
    this.fetchAutosForGasto();
  },
  methods: {
    ...mapActions('gasto', [
      'fetchSolicitudesGasto',
      'guardarSolicitudGasto',
      'updateSolicitudGasto',
      'deleteSolicitudGasto',
      'aprobarSolicitudGasto',
      'rechazarSolicitudGasto',
      'fetchCategoriasGasto',
      'fetchAutosForGasto',
    ]),
    colorEstado(estado) {
      return ESTADOS[estado]?.color || 'grey';
    },
    textoEstado(estado) {
      return ESTADOS[estado]?.title || estado;
    },
    notificar(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
    notificarError(e) {
      this.notificar(e.response?.data?.message || 'Ocurrió un error', 'red');
    },
    recargar() {
      const params = {};
      if (this.filtros.estado) params.estado = this.filtros.estado;
      if (this.filtros.categoria_gasto_id) params.categoria_gasto_id = this.filtros.categoria_gasto_id;
      if (this.filtros.fecha_inicio) params.fecha_inicio = this.filtros.fecha_inicio;
      if (this.filtros.fecha_fin) params.fecha_fin = this.filtros.fecha_fin;
      this.fetchSolicitudesGasto(params);
    },
    openDialogForNew() {
      this.editedItem = {};
      this.editedIndex = -1;
      this.dialog = true;
    },
    editItem(item) {
      this.editedItem = {
        id: item.id,
        categoria_gasto_id: item.categoria_gasto_id,
        auto_id: item.auto_id,
        fecha: item.fecha?.slice(0, 10),
        descripcion: item.descripcion,
        monto: Number(item.monto),
        comprobante: item.comprobante || '',
      };
      this.editedIndex = this.solicitudesGasto.findIndex(s => s.id === item.id);
      this.dialog = true;
    },
    confirmDelete(item) {
      this.editedItem = { ...item };
      this.dialogDelete = true;
    },
    async deleteItemConfirm() {
      try {
        await this.deleteSolicitudGasto(this.editedItem.id);
      } catch (e) {
        this.notificarError(e);
      }
      this.dialogDelete = false;
    },
    async saveDialog(item) {
      const action = this.editedIndex === -1 ? this.guardarSolicitudGasto : this.updateSolicitudGasto;
      try {
        await action(item);
        this.dialog = false;
        this.notificar(this.editedIndex === -1 ? 'Solicitud enviada' : 'Solicitud actualizada');
        this.recargar();
      } catch (e) {
        this.notificarError(e);
      }
    },
    abrirRevision(item, accion) {
      this.revision = { item, accion, observacion: '' };
      this.dialogRevision = true;
    },
    async confirmarRevision() {
      const { item, accion, observacion } = this.revision;
      const action = accion === 'aprobar' ? this.aprobarSolicitudGasto : this.rechazarSolicitudGasto;
      this.guardandoRevision = true;
      try {
        await action({ id: item.id, observacion: observacion || null });
        this.dialogRevision = false;
        this.notificar(accion === 'aprobar' ? 'Solicitud aprobada y registrada como gasto' : 'Solicitud rechazada');
        this.recargar();
      } catch (e) {
        this.notificarError(e);
      } finally {
        this.guardandoRevision = false;
      }
    },
  },
};
</script>
